import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";

import { PagePrint } from "../PagePrint";
import { useGetAttachments } from "../../hooks/react-query/useAttachmentsData";
import api from "../../utils/api";
import {
  FILE_CATEGORIES,
  RESOURCE_TYPES,
  getCategoryLabel,
} from "../../constants/attachments";

const VISIBLE_CATEGORIES = [FILE_CATEGORIES.MODEL, FILE_CATEGORIES.SETUP];

const isImage = (mimeType) => /^image\//i.test(mimeType || "");

// Fetches the image via the authenticated `api` client and exposes it as a
// blob: URL. This sidesteps two problems with using the download endpoint
// directly as an <img src>:
//   1. setting crossOrigin="anonymous" strips the auth cookie → 401
//   2. omitting crossOrigin means html2canvas taints the canvas and the PDF
//      capture fails when the image is on a different origin
// A same-origin blob URL renders and is safe to canvas-capture.
const useAttachmentBlobUrl = (id) => {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!id) return undefined;
    let revoked = false;
    let createdUrl = null;

    api
      .get(`/attachments/${id}/download?inline=true`, { responseType: "blob" })
      .then((blob) => {
        if (revoked) return;
        createdUrl = URL.createObjectURL(blob);
        setUrl(createdUrl);
      })
      .catch(() => {
        // Leave url null — the <img> alt text will surface the failure.
      });

    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [id]);

  return url;
};

const AttachmentImage = ({ attachment }) => {
  const url = useAttachmentBlobUrl(attachment.id);
  return (
    <Stack
      gap="6px"
      sx={{
        padding: "8px",
        overflow: "hidden",
      }}
    >
      <Typography variant="boldCaption" fontSize={11}>
        {getCategoryLabel(attachment.category)?.label}: {attachment.fileName}
      </Typography>
      {url ? (
        <Box
          component="img"
          src={url}
          alt={attachment.fileName}
          sx={{
            width: "100%",
            height: "auto",
            maxHeight: "4in",
            objectFit: "contain",
            display: "block",
          }}
        />
      ) : (
        <Typography variant="caption" color="text.secondary" fontSize={10}>
          Loading image…
        </Typography>
      )}
    </Stack>
  );
};

// Renders Model + Setup image attachments for the quote on a dedicated
// printable page so they get included after the costing sheet (and in the
// generated PDF). Hidden entirely when there are no eligible images.
const CostingAttachmentsPage = ({ quoteId }) => {
  const { data } = useGetAttachments({
    resourceType: RESOURCE_TYPES.QUOTE,
    resourceId: quoteId,
  });

  const images = useMemo(
    () =>
      (data || []).filter(
        (a) => VISIBLE_CATEGORIES.includes(a.category) && isImage(a.mimeType),
      ),
    [data],
  );

  if (!quoteId || !images.length) return null;

  return (
    <PagePrint
      cardProps={{ id: "costing-attachments", sx: { position: "relative" } }}
    >
      <Stack gap="12px">
        <Typography variant="boldCaptionL">Reference Attachments</Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "12px",
            alignItems: "start",
          }}
        >
          {images.map((a) => (
            <AttachmentImage key={a.id} attachment={a} />
          ))}
        </Box>
      </Stack>
    </PagePrint>
  );
};

export default CostingAttachmentsPage;
